import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import type { ShopRatingRow } from '../api/types';
import { count, percent } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { CHROME, SERIES, STATUS } from './palette';

const RIVAL = SERIES[0];
const US = SERIES[3];

/**
 * Two measures per shop, so a scatter rather than two rankings. The reference
 * line at 100 is the market low: a shop to its right is charging a premium, and
 * a shop above our own marker is better rated than us.
 *
 * Our own shop is a distinct hue AND the only labelled point, so identity never
 * rests on colour alone.
 */
export function RatingPriceScatter({ rows }: { rows: ShopRatingRow[] }) {
  const data = rows.filter((row) => row.avgRatingPercent !== null && row.priceIndexPct !== null);

  if (data.length < 2) {
    return <div className="state">Not enough rated shops yet.</div>;
  }

  const ours = data.find((row) => row.isOurShop);

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ScatterChart margin={{ top: 12, right: 20, bottom: 28, left: 4 }}>
        <CartesianGrid stroke={CHROME.grid} />
        <XAxis
          type="number"
          dataKey="priceIndexPct"
          name="Price level"
          domain={['dataMin - 2', 'dataMax + 2']}
          tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
          axisLine={{ stroke: CHROME.axis }}
          tickLine={false}
          tickFormatter={(value: number) => `${Math.round(value)}%`}
          label={{
            value: 'Price as % of the market low',
            position: 'insideBottom',
            offset: -16,
            fill: CHROME.inkMuted,
            fontSize: 11,
          }}
        />
        <YAxis
          type="number"
          dataKey="avgRatingPercent"
          name="Rating"
          domain={['dataMin - 2', 'dataMax + 2']}
          tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={54}
          tickFormatter={(value: number) => `${Math.round(value)}%`}
        />
        <ZAxis type="number" dataKey="offers" range={[60, 320]} />
        <ReferenceLine x={100} stroke={CHROME.inkMuted} strokeDasharray="4 3" />
        {ours && <ReferenceLine y={ours.avgRatingPercent} stroke={US} strokeDasharray="4 3" />}
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) {
              return null;
            }

            const row = payload[0].payload as ShopRatingRow;

            return (
              <ChartTooltip
                title={row.shopName}
                note={row.isOurShop ? 'your shop' : undefined}
                rows={[
                  { key: 'rating', label: 'Mean rating', value: percent(row.avgRatingPercent) },
                  { key: 'ratings', label: 'Ratings', value: count(row.ratingCount) },
                  {
                    key: 'price',
                    label: 'Price level',
                    value: `${row.priceIndexPct.toFixed(1)}% of market low`,
                    color: row.isOurShop ? US : RIVAL,
                  },
                  { key: 'offers', label: 'Offers seen', value: count(row.offers) },
                ]}
              />
            );
          }}
        />
        <Scatter isAnimationActive={false} data={data} fill={RIVAL}>
          {data.map((row) => (
            <Cell
              key={row.shopName}
              fill={row.isOurShop ? US : RIVAL}
              stroke={row.isOurShop ? STATUS.good : CHROME.surface}
              strokeWidth={2}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}
